import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import MobileBottomNav from './MobileBottomNav';
import { useApp } from '../context/AppContext';
import { MdCheckCircle, MdError, MdWarning, MdInfo } from 'react-icons/md';
import {
  startEmergencyAlarm, stopEmergencyAlarm, isEmergencySoundMuted,
  setEmergencySoundMuted, unlockEmergencyAudio, ensureEmergencyNotificationPermission,
  notifyEmergencyInBackground, startTitleFlash, stopTitleFlash,
} from '../utils/emergencyAlarm';
import ErrorBoundary from './ErrorBoundary';
import { useIsMobile, useIsTablet } from '../hooks/useBreakpoint';
import { EMERGENCY_UI_ENABLED } from '../config/hospitalNav';

const ICONS = { success:<MdCheckCircle style={{color:'var(--color-success)',fontSize:18}}/>, error:<MdError style={{color:'var(--color-danger)',fontSize:18}}/>, warning:<MdWarning style={{color:'var(--color-warning)',fontSize:18}}/>, info:<MdInfo style={{color:'var(--color-secondary)',fontSize:18}}/> };

function useGlobalAlarm(newCount) {
  const [alarmRinging, setAlarmRinging] = useState(false);
  const [muted, setMuted] = useState(() => isEmergencySoundMuted());

  const stop = useCallback(() => {
    stopEmergencyAlarm();
    stopTitleFlash();
    setAlarmRinging(false);
  }, []);

  const start = useCallback(async () => {
    if (isEmergencySoundMuted()) return;
    await unlockEmergencyAudio();
    await startEmergencyAlarm();
    setAlarmRinging(true);
  }, []);

  useEffect(() => {
    if (newCount > 0 && !muted) {
      start();
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        startTitleFlash(newCount);
      }
    } else {
      stop();
    }
  }, [newCount, muted, start, stop]);

  useEffect(() => () => stop(), [stop]);

  useEffect(() => {
    const onNew = (e) => {
      const { alerts, hidden, count } = e.detail || {};
      if (hidden) {
        notifyEmergencyInBackground(alerts || []);
        if (count > 0) startTitleFlash(count);
        if (!muted && count > 0) start();
      }
    };
    window.addEventListener('hcen:new-emergency', onNew);
    return () => window.removeEventListener('hcen:new-emergency', onNew);
  }, [muted, start]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        stopTitleFlash();
        if (newCount > 0 && !muted) unlockEmergencyAudio().then(() => start());
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [newCount, muted, start]);

  const toggleMute = useCallback(() => {
    setMuted(m => {
      const next = !m;
      setEmergencySoundMuted(next);
      if (next) stop();
      return next;
    });
  }, [stop]);

  return { alarmRinging, muted, toggleMute, stop };
}

export default function Layout() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1400;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { toasts, emergencyAlertCount } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    let prevWidth = window.innerWidth;
    const onResize = () => {
      const w = window.innerWidth;
      if (isMobile) {
        setCollapsed(true);
      } else if (w <= 1400 && prevWidth > 1400) {
        setCollapsed(true);
      } else if (w > 1400 && prevWidth <= 1400) {
        setCollapsed(false);
      }
      prevWidth = w;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  useEffect(() => {
    if (!EMERGENCY_UI_ENABLED) return undefined;
    const unlock = () => {
      unlockEmergencyAudio();
      ensureEmergencyNotificationPermission();
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  const newCount = EMERGENCY_UI_ENABLED ? (emergencyAlertCount || 0) : 0;
  const { alarmRinging, muted, toggleMute } = useGlobalAlarm(newCount);

  const mainClass = [
    'main-content',
    !isMobile && collapsed ? 'collapsed' : '',
    isMobile ? 'is-mobile' : '',
    isTablet && !isMobile ? 'is-tablet' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`app-layout${isMobile ? ' is-mobile' : ''}`}>
      <Sidebar
        collapsed={isMobile ? false : collapsed}
        onToggle={() => setCollapsed(p => !p)}
        isMobile={isMobile}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div className={mainClass}>
        <Topbar
          collapsed={collapsed}
          isMobile={isMobile}
          onMenuOpen={() => setMobileMenuOpen(true)}
        />
        <div className="page-container anim-fadein">
          <ErrorBoundary scope="page">
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>

      {isMobile && (
        <MobileBottomNav onOpenMenu={() => setMobileMenuOpen(true)} />
      )}

      {EMERGENCY_UI_ENABLED && alarmRinging && !muted && (
        <div className={`emergency-alarm-banner${isMobile ? ' is-mobile' : ''}${!isMobile && collapsed ? ' is-collapsed' : ''}`}>
          <span style={{ fontSize: 28 }}>&#x1F6A8;</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#fff', fontFamily: 'Outfit,sans-serif' }}>
              {newCount} New Emergency Alert{newCount > 1 ? 's' : ''}!
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              Patients need immediate action
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/emergency-alerts')}
            className="emergency-alarm-banner__btn emergency-alarm-banner__btn--primary"
          >
            View
          </button>
          <button
            type="button"
            onClick={toggleMute}
            className="emergency-alarm-banner__btn"
          >
            Mute
          </button>
        </div>
      )}

      <div className={`toast-wrap${isMobile ? ' is-mobile' : ''}`}>
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {ICONS[t.type] || ICONS.info}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
